import { Button, Flex, Input } from '@dnb/eufemia';
import { send_medium as SendMediumIcon } from '@dnb/eufemia/icons';
import { useEufemiaForm } from '@portals/shared-frontend/hooks';
import { type JSX, useCallback, useState } from 'react';
import { z } from 'zod';

import { forgotPassword } from '@/auth/cognito';
import Container from '@/components/Container';
import LoadingModal from '@/components/LoadingModal';
import ModalPage from '@/components/ModalPage';
import useAuth from '@/hooks/useAuth';

import VerifyNewPassword from './VerifyNewPassword';

export default function ForgotPassword(): JSX.Element {
  const [verifyNewPassword, setVerifyNewPassword] = useState<boolean>(false);

  const { isInternal } = useAuth(); // Access isInternal from useAuth

  // Define the schema dynamically based on isInternal
  const forgotPasswordFormSchema = z
    .object({
      email: z.string().email(),
    })
    .refine(
      () => !isInternal, // Use isInternal to determine if the user is internal
      {
        message:
          'DNB users use single sign-on login. If you want to reset your password, you have to change your password on your DNB account.',
        path: ['email'],
      },
    );

  const {
    controller: { values, formError },
    register,
    handleSubmit,
    submitting,
  } = useEufemiaForm(forgotPasswordFormSchema, {});

  const onSubmit = handleSubmit(
    useCallback(async ({ email }) => {
      await forgotPassword(email);
      setVerifyNewPassword(true);
    }, []),
  );

  if (verifyNewPassword && !!values.email) {
    return <VerifyNewPassword email={values.email} />;
  }

  return (
    <ModalPage subtitle="Forgotten password" title="Verify account">
      {submitting && <LoadingModal />}

      <Container size="small">
        <form onSubmit={onSubmit}>
          <Flex.Horizontal align="baseline">
            <Input
              autoFocus
              label="Fill in your email address so we can send you a verification code."
              placeholder="mail@example.com"
              size="large"
              stretch
              type="email"
              {...register.input('email')}
            />
          </Flex.Horizontal>
          <Flex.Horizontal align="baseline" top="large">
            <Button
              disabled={submitting}
              icon={SendMediumIcon}
              iconPosition="left"
              iconSize="medium"
              size="large"
              status={formError}
              type="submit"
            >
              Send verification code
            </Button>
          </Flex.Horizontal>
        </form>
      </Container>
    </ModalPage>
  );
}
